import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateAppointmentMutation } from "../../utils/apiSlice";
import { getEffectiveUserData } from "../../utils/activeProfileStorage";
import { tryEarnUserAction } from "../../utils/gamificationService";
import { useDispatch } from "react-redux";
import { hydrateGamification } from "../../utils/gamificationSlice";
import { emailValidation, nameValidation, phoneValidation } from "../../utils/validation";
import * as Yup from "yup";
import {
    HomeOutlined
} from "@ant-design/icons";

const doctorSpecialties = [
    { id: 1, name: "Cardiologist" },
    { id: 2, name: "Dermatologist" },
    { id: 3, name: "Orthopedic" },
    { id: 4, name: "Neurologist" },
];

const doctorData: any = {
    1: [
        { id: 9, name: "Dr. John Heart" },
        { id: 10, name: "Dr. Smith Cardio" }
    ],
    2: [
        { id: 11, name: "Dr. Anna Skin" },
        { id: 12, name: "Dr. Rose Derm" }
    ],
    3: [
        { id: 13, name: "Dr. Mike Bone" },
        { id: 14, name: "Dr. Kevin Ortho" }
    ],
    4: [
        { id: 15, name: "Dr. Sarah Neuro" },
        { id: 16, name: "Dr. David Brain" }
    ],
};

const Appointment = () => {
    const dispatch = useDispatch();
    const [uploading, setUploading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [showConfirm, setShowConfirm] = useState(false);
    const [createAppointment, { isLoading }] = useCreateAppointmentMutation();

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        relationship: "",
        dateOfBirth: "",
        email: "",
        contactNumber: "",
        gender: "",
        address: "",
        consultingDoctor: "",
        consultingDoctorId: "" as string | number,
        doctorSpecialty: "" as string | number,
        dateOfAppointment: "",
        timeOfAppointment: "",
        problem: "",
        symptoms: "",
        note: "",
        report: null as File | null
    });

    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        const selectedRel = location.state?.relationship;
        if (selectedRel) {
            const storedFamily = localStorage.getItem("familyMembers");
            if (storedFamily) {
                const family = JSON.parse(storedFamily);
                // The modal might pass the relationship ID or Type
                const member = family.find((m: any) =>
                    m.relationship?.toLowerCase() === selectedRel.toLowerCase() ||
                    m.relationshipId?.toString() === selectedRel
                );

                if (member) {
                    setFormData(prev => ({
                        ...prev,
                        relationship: member.relationship || selectedRel,
                        firstName: member.profile?.firstName || "",
                        lastName: member.profile?.lastName || "",
                        dateOfBirth: member.profile?.dateOfBirth || "",
                        gender: member.profile?.gender || "",
                        email: member.profile?.email || "",
                        contactNumber: member.profile?.phoneNumber || ""
                    }));
                } else if (selectedRel === "Self") {
                    const primaryProfile = localStorage.getItem("patientProfile");
                    if (primaryProfile) {
                        const profile = JSON.parse(primaryProfile);
                        setFormData(prev => ({
                            ...prev,
                            relationship: "Self",
                            firstName: profile.firstName || "",
                            lastName: profile.lastName || "",
                            dateOfBirth: profile.dateOfBirth || "",
                            gender: profile.gender || "",
                            email: profile.email || "",
                            contactNumber: profile.phoneNumber || ""
                        }));
                    }
                } else {
                    // If no member found in local storage, at least set the relationship
                    setFormData(prev => ({ ...prev, relationship: selectedRel }));
                }
            } else {
                setFormData(prev => ({ ...prev, relationship: selectedRel }));
            }
        }
    }, [location.state]);

const handleFileChange = async (e: any) => {
  const file = e.target.files[0];
  if (!file) return;

  setUploading(true); // ✅ start loader

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("report", file);

    // simulate delay (2 sec)
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setFormData((prev) => ({
      ...prev,
      report: file,
    }));
  } catch (err) {
    console.log(err);
  } finally {
    setUploading(false); // ✅ stop loader after 2 sec
  }
};

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;

        if (name === "contactNumber") {
            let onlyNums = value.replace(/\D/g, "").slice(0, 10);

            setFormData((prev) => ({ ...prev, [name]: onlyNums }));

            const phoneRegex = /^[6-9]\d{9}$/;

            // Show error only when user has typed something
            if (onlyNums.length > 0 && !phoneRegex.test(onlyNums)) {
                setErrors((prev: any) => ({
                    ...prev,
                    contactNumber: "Enter valid 10-digit number starting with 6-9",
                }));
            } else {
                setErrors((prev: any) => ({
                    ...prev,
                    contactNumber: undefined,
                }));
            }

            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors((prev: any) => ({ ...prev, [name]: undefined }));
        }
    };
    const validate = () => {
        const newErrors: any = {};
        
        try { nameValidation.validateSync(formData.firstName); } catch (e: any) { newErrors.firstName = e.message; }
        try { nameValidation.validateSync(formData.lastName); } catch (e: any) { newErrors.lastName = e.message; }
        
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
        
        try { emailValidation.validateSync(formData.email); } catch (e: any) { newErrors.email = e.message; }
        try { phoneValidation.validateSync(formData.contactNumber); } catch (e: any) { newErrors.contactNumber = e.message; }

        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.consultingDoctor) newErrors.consultingDoctor = "Consulting doctor is required";
        if (!formData.doctorSpecialty) newErrors.doctorSpecialty = "Doctor specialty is required";
        if (!formData.dateOfAppointment) newErrors.dateOfAppointment = "Date of appointment is required";
        if (!formData.timeOfAppointment) newErrors.timeOfAppointment = "Time of appointment is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSchedule = async () => {
        if (validate()) {
            const eff = getEffectiveUserData();
            const patientId = eff?.patientId || null;

            // Helper to convert 12h format from slots to 24h ISO time
            const convertTo24h = (timeStr: string) => {
                let [hours, minutes] = timeStr.trim().split(":").map(Number);
                // For this specific clinical schedule: 
                // 12:00-12:59 stays as is (midday)
                // 01:00-08:59 is PM (add 12)
                // 10:00-11:59 is AM (stays as is)
                if (hours < 9) {
                    hours += 12;
                }
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            };

            const timeRange = formData.timeOfAppointment.split(" - ");
            const startTime24 = convertTo24h(timeRange[0]);
            const endTime24 = convertTo24h(timeRange[1]);

            // Construct ISO strings with timezone offset +05:30
            const scheduledStart = `${formData.dateOfAppointment}T${startTime24}:00+05:30`;
            const scheduledEnd = `${formData.dateOfAppointment}T${endTime24}:00+05:30`;

            const payload = {
                patientId: patientId,
                relationship: formData.relationship?.toLowerCase() || "self",
                dateOfBirth: formData.dateOfBirth,
                contactNumber: formData.contactNumber,
                gender: formData.gender?.toLowerCase() || "male",
                address: formData.address,
                consultingDoctor: formData.consultingDoctor,
                consultingDoctorId: formData.consultingDoctorId,
                doctorSpecialty: formData.doctorSpecialty,
                problem: formData.problem,
                symptoms: formData.symptoms,
                note: formData.note,
                appointmentType: "CONSULTATION",
                scheduledStart: scheduledStart,
                scheduledEnd: scheduledEnd,
                reason: formData.problem || "General checkup",
                appointmentSource: "WEB_APP",
                meetingUrl: "",
                cancellationReason: "",
                reminderStatus: ""
            };

            console.log("Appointment Data (JSON):", JSON.stringify(payload, null, 2));

            try {
                const response = await createAppointment(payload).unwrap();
                if (response?.success || response) {
                    toast.success(response?.message || "Appointment scheduled successfully!");
                    const uid = eff?.userId || eff?.id;
                    if (uid) {
                        const coin = tryEarnUserAction(String(uid), "appointment_booked");
                        if (coin.ok) {
                            dispatch(hydrateGamification(String(uid)));
                        }
                    }
                    setTimeout(() => {
                        navigate("/appointmentList");
                    }, 1500);
                }
            } catch (error: any) {
                toast.error(error?.data?.message || "Failed to schedule appointment. Please try again.");
            }
        } else {
            toast.error("Please fill all required fields");
        }
    };

    const timeSlots = [
        "10:30 - 11:00", "11:00 - 11:30", "11:30 - 12:00",
        "12:00 - 12:30", "12:30 - 01:00", "03:30 - 04:00",
        "04:00 - 04:30", "04:30 - 05:00", "05:30 - 06:00",
    ];

    return (
        <div className="content-wrapper">
            <div className="container-fluid table-padding">

                {/* PAGE HEADER */}
                <div className="page-header mb-4">
                    <div className="breadcrumb">
                        <h3 className="page-title">Book Appointment</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon"><HomeOutlined /></span>
                            <span className="separator"> - </span>
                            <span>Appointment</span>
                            <span className="separator"> - </span>
                            <span>Book</span>
                        </span>
                    </div>
                </div>

                {/* FORM CARD */}
                <div className="card">
                    <div className="card-header bg-white border-bottom">
                        <h5 className="mb-0 fw-semibold">
                            📝 Book Appointment
                        </h5>
                    </div>
                    <div className="card-body">

                        {/* ================= PERSONAL INFO ================= */}
                        <h5 className="mb-3 border-bottom pb-2">
                            👤 Personal Info
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-4">
                                <label htmlFor="firstName" className="form-label">First Name</label>
                                <input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} type="text" className={`form-control ${errors.firstName ? 'is-invalid' : ''}`} placeholder="First Name" />
                                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="lastName" className="form-label">Last Name</label>
                                <input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} type="text" className={`form-control ${errors.lastName ? 'is-invalid' : ''}`} placeholder="Last Name" />
                                {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
                                <input id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} type="date" className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`} max={new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
                                    .toISOString()
                                    .split("T")[0]} />
                                {errors.dateOfBirth && <div className="invalid-feedback">{errors.dateOfBirth}</div>}
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="email" className="form-label">Email</label>
                                <input id="email" name="email" value={formData.email} onChange={handleInputChange} type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} placeholder="Email (optional)" />
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="contactNumber" className="form-label">Contact Number</label>
                                <input id="contactNumber" name="contactNumber" value={formData.contactNumber} onChange={handleInputChange} type="text" maxLength={10} className={`form-control ${errors.contactNumber ? 'is-invalid' : ''}`} placeholder="Phone" />
                                {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
                            </div>


                            <div className="col-md-4">
                                <label htmlFor="gender" className="form-label">Gender</label>
                                <select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} className={`form-select ${errors.gender ? 'is-invalid' : ''}`}>
                                    <option value="" disabled hidden>Select</option>
                                    <option value="MALE">Male</option>
                                    <option value="FEMALE">Female</option>
                                    {/* <option value="NON_BINARY">Non-binary</option>
                                    <option value="TRANSGENDER_MAN">Transgender man</option>
                                    <option value="TRANSGENDER_WOMAN">Transgender woman</option>
                                    <option value="OTHER">Other</option> */}
                                    <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                                </select>
                                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                            </div>

                            <div className="col-12">
                                <label htmlFor="address" className="form-label">Address</label>
                                <textarea id="address" name="address" value={formData.address} onChange={handleInputChange} className="form-control" rows={2} placeholder="Address" />
                            </div>
                        </div>

                        {/* ================= APPOINTMENT DETAILS ================= */}
                        <h5 className="mt-4 mb-3 border-bottom pb-2">
                            📅 Appointment Details
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-4">
                                <label htmlFor="doctorSpecialty" className="form-label">Doctor Specialty</label>
                                <select
                                    id="doctorSpecialty"
                                    name="doctorSpecialty"
                                    className={`form-select ${errors.doctorSpecialty ? 'is-invalid' : ''}`}
                                    value={formData.doctorSpecialty}
                                    onChange={(e) => {
                                        const specialtyId = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            doctorSpecialty: specialtyId ? Number(specialtyId) : "",
                                            consultingDoctor: "",
                                            consultingDoctorId: ""
                                        }));
                                    }}
                                >
                                    <option value="" disabled hidden>Select Specialty</option>
                                    {doctorSpecialties.map((spec) => (
                                        <option key={spec.id} value={spec.id}>
                                            {spec.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.doctorSpecialty && <div className="invalid-feedback">{errors.doctorSpecialty}</div>}
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="consultingDoctor" className="form-label">Consulting Doctor</label>
                                <select
                                    id="consultingDoctor"
                                    name="consultingDoctor"
                                    value={formData.consultingDoctorId}
                                    onChange={(e) => {
                                        const doctorId = e.target.value;
                                        const specialtyDoctors = doctorData[formData.doctorSpecialty];
                                        const doctor = specialtyDoctors?.find((d: any) => d.id.toString() === doctorId);
                                        setFormData(prev => ({
                                            ...prev,
                                            consultingDoctorId: doctorId ? Number(doctorId) : "",
                                            consultingDoctor: doctor ? doctor.name : ""
                                        }));
                                    }}
                                    className={`form-select ${errors.consultingDoctor ? 'is-invalid' : ''}`}
                                >
                                    <option value="" disabled hidden>Select Consulting Doctor</option>
                                    {formData.doctorSpecialty && doctorData[formData.doctorSpecialty]?.map((doc: any) => (
                                        <option key={doc.id} value={doc.id}>{doc.name}</option>
                                    ))}
                                </select>
                                {errors.consultingDoctor && <div className="invalid-feedback">{errors.consultingDoctor}</div>}
                            </div>


                            <div className="col-md-4">
                                <label htmlFor="dateOfAppointment" className="form-label">Date of Appointment</label>
                                <input id="dateOfAppointment" name="dateOfAppointment" value={formData.dateOfAppointment} onChange={handleInputChange} type="date" className={`form-control ${errors.dateOfAppointment ? 'is-invalid' : ''}`} min={new Date().toISOString().split("T")[0]} />
                                {errors.dateOfAppointment && <div className="invalid-feedback">{errors.dateOfAppointment}</div>}
                            </div>

                            <div className="col-12">
                                <label className="form-label d-block">Time of Appointment</label>
                                <div className="d-flex flex-wrap">
                                    {timeSlots.map((time) => (
                                        <div
                                            key={time}
                                            role="button"
                                            tabIndex={0}
                                            className={`time-slot m-1 ${formData.timeOfAppointment === time ? 'active' : ''} ${errors.timeOfAppointment ? 'border-danger' : ''}`}
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, timeOfAppointment: time }));
                                                if (errors.timeOfAppointment) setErrors((prev: any) => ({ ...prev, timeOfAppointment: undefined }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    setFormData(prev => ({ ...prev, timeOfAppointment: time }));
                                                    if (errors.timeOfAppointment) setErrors((prev: any) => ({ ...prev, timeOfAppointment: undefined }));
                                                }
                                            }}
                                        >
                                            {time}
                                        </div>
                                    ))}
                                </div>
                                {errors.timeOfAppointment && <div className="text-danger small mt-1">{errors.timeOfAppointment}</div>}
                            </div>

                            <div className="col-12">
                                <label htmlFor="uploadReport" className="form-label">Upload Report</label>
                                <div className="upload-box">
                                    <input type="file" className="upload-input" id="uploadReport" onChange={handleFileChange} />
                                    <label htmlFor="uploadReport" className="upload-label">
                                       <div className="upload-content text-center">
  {uploading ? (
    <>
      <div className="spinner-border text-primary mb-2" role="status" />
      <p className="mb-0">Uploading...</p>
    </>
  ) : (
    <>
      <i className="bi bi-upload upload-icon"></i>
      <p className="mb-1 fw-semibold">
        {formData.report ? formData.report.name : "Drag & drop your report here"}
      </p>
      {!formData.report && (
        <p className="text-muted small mb-2">or click to browse</p>
      )}
      <span className="upload-hint">(PDF, JPG, PNG, DOCX • max 10MB)</span>
    </>
  )}
</div>
                                    </label>
                                </div>
                            </div>

                            <div className="col-12">
                                <label htmlFor="problem" className="form-label">Problem</label>
                                <textarea id="problem" name="problem" value={formData.problem} onChange={handleInputChange} className="form-control" rows={2} />
                            </div>
                            <div className="col-12">
                                <label htmlFor="symptoms" className="form-label">Symptoms</label>
                                <textarea id="symptoms" name="symptoms" value={formData.symptoms} onChange={handleInputChange} className="form-control" rows={2} />
                            </div>

                            <div className="col-12">
                                <label htmlFor="note" className="form-label">Note</label>
                                <textarea id="note" name="note" value={formData.note} onChange={handleInputChange} className="form-control" rows={2} placeholder="If you're taking any medicine, please type it here." />
                            </div>
                        </div>

                        {/* ================= ACTION BUTTONS ================= */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-warning text-white" onClick={() => navigate(-1)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleSchedule} disabled={isLoading}>
                                {isLoading ? "Scheduling..." : "Schedule Appointment"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                open={showConfirm}
                maxWidth="sm"
                fullWidth
                className="auth-modal glass-modal"
                onClose={() => setShowConfirm(false)}
                slotProps={{
                    backdrop: {
                        style: { backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(5px)" },
                    }
                }}
            >
                <DialogTitle className="pt-30 pb-10 text-center">
                    <div className="h3 mb-1">Your appointment has been scheduled!</div>
                </DialogTitle>

                <DialogContent className="px-40 pb-30 text-center">
                    <div className="tick-animation mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
                        <svg width="80" height="80" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="50" cy="50" r="48" stroke="#4CAF50" strokeWidth="4" fill="none" />
                            <path d="M30 50L45 65L70 35" stroke="#4CAF50" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    <p className="mb-3">
                        Please note: To provide complete information, we recommend filling out the health questionnaire form for this family member.
                    </p>
                    <p className="text-muted fs-14">
                        Would you like to fill the questionnaire now?
                    </p>
                </DialogContent>

                <DialogActions className="pb-30 px-40 d-flex justify-content-center gap-3">
                    <button className="btn btn-secondary" onClick={() => { setShowConfirm(false); navigate("/appointmentList"); }}>
                        Close
                    </button>
                    <button className="btn btn-primary" onClick={() => { setShowConfirm(false); navigate("/dashboard"); }}>
                        Fill Questionnaire
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Appointment;

